import { Controller } from "@hotwired/stimulus"
import Honeybadger from "@honeybadger-io/js"
import { handoff } from "../helpers/position_grid_handoff"
import { positionRowId } from "../helpers/position_grid_rows"
import { renderStreamMessage } from "../helpers/stream_render"

// The position grid's side panels (billing/position_grid/_grid.html.erb): the Langtext
// (Shift+F2), the Positionsdetails (Strg/Cmd+I), the Kalkulation (Strg/Cmd+Shift+K) and the
// catalog (Strg/Cmd+K). position-tree and position-structure beside it own folding and structure;
// all three claim their keys through `grid-cells:command`.
//
// The Langtext is a cell like any other — its editor comes from, and saves to, the cells
// endpoint, so a save is validated, rolled up and undoable — and only lives in the drawer
// because it runs to many lines. The details are the row form in the drawer's turbo frame; their
// save re-renders the whole grid, so the selection is handed across like a structure step's. The
// Kalkulation and the catalog are the page's own drawers, opened for the selected row.
export default class extends Controller {
  static targets = ["drawer", "longText", "longTextBody", "number", "title", "frame", "form"]
  // `numberColumn` is the grid's OZ column (`position_number` on a billing document).
  static values = {
    itemsUrl: String,
    editable: Boolean,
    storageKey: String,
    numberColumn: String,
    messages: Object,
  }

  connect() {
    this.mode = null
    this.onDrawerKeydown = (event) => this.drawerKeydown(event)
    this.onFrameLoad = () => this.focusForm()
    this.onSubmitStart = () => this.handOffSelection()
    this.onSubmitEnd = (event) => this.withdrawSelection(event)
    if (this.hasDrawerTarget) {
      this.drawerTarget.addEventListener("keydown", this.onDrawerKeydown)
      this.frameTarget.addEventListener("turbo:frame-load", this.onFrameLoad)
      this.frameTarget.addEventListener("turbo:submit-start", this.onSubmitStart)
      this.frameTarget.addEventListener("turbo:submit-end", this.onSubmitEnd)
    }
  }

  disconnect() {
    if (!this.hasDrawerTarget) return

    this.drawerTarget.removeEventListener("keydown", this.onDrawerKeydown)
    this.frameTarget.removeEventListener("turbo:frame-load", this.onFrameLoad)
    this.frameTarget.removeEventListener("turbo:submit-start", this.onSubmitStart)
    this.frameTarget.removeEventListener("turbo:submit-end", this.onSubmitEnd)
  }

  row(id) {
    return id ? this.element.querySelector(`tr[data-grid-row][data-row-id="${id}"]`) : null
  }

  // -- keys -------------------------------------------------------------------

  // A Kalkulation row (`data-kind="component"`, the costing view) is no line item: every panel it
  // asks for is its position's — Strg+Shift+K there opens the calculation it belongs to.
  command(event) {
    const { originalEvent: key, column } = event.detail
    const rowId = this.positionRowId(event.detail.rowId)
    const mod = key.ctrlKey || key.metaKey
    const letter = key.key.toLowerCase()
    const plain = !key.altKey && !key.shiftKey

    // A Textposition is its text: Enter, F2 and Shift+F2 all open it — there is no cell to edit.
    if (this.row(rowId)?.dataset.kind === "text" && this.opensText(key, mod)) {
      event.preventDefault()
      this.openTextContent(rowId, column)
    } else if (key.key === "F2" && key.shiftKey && !mod && !key.altKey) {
      event.preventDefault()
      this.openLongText(rowId, column)
    } else if (mod && plain && letter === "i") {
      event.preventDefault()
      this.openDetails(rowId, column)
    } else if (mod && key.shiftKey && !key.altKey && letter === "k") {
      event.preventDefault()
      this.openCalculation(rowId)
    } else if (mod && plain && letter === "k") {
      event.preventDefault()
      this.openCatalog(rowId)
    }
  }

  positionRowId(rowId) {
    return positionRowId(this.row(rowId), rowId)
  }

  opensText(key, mod) {
    if (mod || key.altKey) return false
    return key.key === "Enter" || key.key === "F2"
  }

  // Inside the drawer: Esc closes it, Strg/Cmd+Enter saves. Enter alone stays a line break in
  // the Langtext and a field's own key in the details form.
  drawerKeydown(event) {
    if (event.key === "Escape") {
      event.preventDefault()
      this.close()
    } else if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
      event.preventDefault()
      if (this.mode === "long_text" || this.mode === "text_content") this.saveLongText()
      else if (this.hasFormTarget) this.formTarget.requestSubmit()
    }
  }

  // -- Langtext ---------------------------------------------------------------

  // `data-long-text` marks a row whose Langtext the cells endpoint accepts (CellEditPolicy): not
  // a Titel, not a Nachlass line. The editor is fetched first; if another panel was asked for
  // meanwhile, the late answer is dropped instead of taking the drawer over.
  async openLongText(rowId, column) {
    if (!this.editableValue || !this.hasDrawerTarget) return
    const row = this.row(rowId)
    if (row?.dataset.longText === undefined) {
      const group = row?.dataset.kind === "group"
      return this.notify(
        this.messagesValue[group ? "group_has_no_long_text" : "discount_has_no_long_text"],
      )
    }

    if (await this.openCellDrawer("long_text", rowId, column)) {
      const field = this.longTextBodyTarget.querySelector("textarea")
      field?.focus()
      field?.setSelectionRange(field.value.length, field.value.length)
    }
  }

  // -- Textposition -----------------------------------------------------------

  // The same drawer, with the rich-text editor of the row's text (position_grid/editors/_text_content,
  // rich_text_editor_controller.js) — which takes the focus itself once it is mounted. A row the
  // account may not change has no `data-text-content`; it keeps its two lines in the grid.
  async openTextContent(rowId, column) {
    if (!this.editableValue || !this.hasDrawerTarget) return
    if (this.row(rowId)?.dataset.textContent === undefined) return

    await this.openCellDrawer("content_html", rowId, column, { mode: "text_content", title: false })
  }

  // A double-click on the row's text (grid-cells clicks the cell's hidden opener).
  openTextContentFromCell(event) {
    const rowId = String(event.params.id)
    this.openTextContent(rowId, "short_text")
  }

  // A Textposition typed into the draft row opens straight away (position-structure#restore).
  openTextContentEvent(event) {
    this.openTextContent(event.detail.rowId, event.detail.column)
  }

  // Fetches a drawer cell's editor first; if another panel was asked for meanwhile, the late
  // answer is dropped instead of taking the drawer over. True once the drawer shows it.
  async openCellDrawer(cell, rowId, column, { mode = cell, title = true } = {}) {
    const row = this.row(rowId)
    const request = (this.pendingOpen = {})
    const html = await this.load(`${this.itemsUrlValue}/${rowId}/cells/${cell}/edit`)
    if (html === null || this.pendingOpen !== request) return false

    this.prepare(mode, { rowId, column })
    this.longTextBodyTarget.innerHTML = html
    this.numberTarget.textContent =
      row.querySelector(`td[data-column="${this.numberColumnValue}"]`)?.textContent.trim() ?? ""
    this.titleTarget.textContent = title
      ? (row.querySelector('td[data-column="short_text"] [title]')?.title ?? "")
      : (this.messagesValue.text_content_title ?? "")
    this.longTextTarget.hidden = false
    this.show()
    return true
  }

  // The answer is the cells endpoint's: on success the rows whose money moved (the Langtext moves
  // none, so its own row) — then the drawer closes; on a rejection the editor with its message,
  // which replaces the one in the drawer, so the text stays as typed. Both are rendered before the
  // selection or the focus moves — a stream renders after the next repaint, and acting on the DOM
  // before that reaches the row or the textarea on its way out.
  async saveLongText() {
    const form = this.longTextBodyTarget.querySelector("form")
    if (!form) return

    try {
      const url = form.getAttribute("action")
      const response = await fetch(url, {
        method: "POST",
        credentials: "same-origin",
        headers: { Accept: "text/vnd.turbo-stream.html", "X-CSRF-Token": this.csrfToken },
        body: new FormData(form),
      })
      const html = await response.text()
      if (!(response.headers.get("Content-Type") || "").includes("text/vnd.turbo-stream.html"))
        return this.failed()

      const rendered = renderStreamMessage(html)
      if (response.ok) return this.close({ after: rendered })

      await rendered
      this.longTextBodyTarget.querySelector("textarea, [contenteditable]")?.focus()
    } catch (error) {
      Honeybadger.notify(error, { context: { url: form.getAttribute("action") } })
      this.failed()
    }
  }

  // -- details ----------------------------------------------------------------

  openDetails(rowId, column) {
    if (!this.editableValue || !this.hasDrawerTarget) return

    this.prepare("details", { rowId, column })
    this.frameTarget.setAttribute("src", `${this.itemsUrlValue}/${rowId}/edit?panel=1`)
    this.show()
  }

  focusForm() {
    if (this.mode !== "details" || !this.hasFormTarget) return

    this.formTarget.querySelector("input:not([type=hidden]), textarea, select")?.focus()
  }

  // A details save answers with the grid — its changed rows synced, or the whole grid re-rendered;
  // position-structure selects this row again. A rejected save answers with the form only, so
  // nothing picks the entry up — it is withdrawn, or the next grid answer would select this row.
  handOffSelection() {
    if (this.returnTo) handoff.set(this.storageKeyValue, this.returnTo)
  }

  withdrawSelection(event) {
    if (!event.detail.success) handoff.delete(this.storageKeyValue)
  }

  // A re-rendered grid brought a closed drawer with it; a synced one leaves the drawer as it was,
  // so a details save closes it here. The selection is position-structure's (the handoff above).
  synced() {
    if (this.mode !== "details") return

    this.reset()
    this.drawerTarget.hidden = true
  }

  // -- the page's own drawers ---------------------------------------------------

  // The Kalkulation drawer opens itself once its frame has loaded (costing_drawer_controller.js).
  openCalculation(rowId) {
    this.pendingOpen = null
    // The page renders the Kalkulation drawer only for a role that may see calculations.
    const frame = document.getElementById("costing_calculation_form")
    if (!frame) return this.notify(this.messagesValue.calculation_unavailable)
    if (this.row(rowId)?.dataset.kind !== "position")
      return this.notify(this.messagesValue.no_calculation)

    frame.setAttribute("src", `${this.itemsUrlValue}/${rowId}/kalkulation`)
  }

  // Under the selected row, as Strg+Enter places a new one: into a selected Titel at the top,
  // after a selected position among its siblings. The insert re-renders the grid; the new row is
  // the one not there before, and the picker closes once it has landed. Closed without an insert
  // (Esc, or a refused one), the entry is withdrawn, so a later mouse insert keeps the picker open.
  openCatalog(rowId) {
    if (!this.editableValue) return
    this.pendingOpen = null
    const host = this.element.closest('[data-controller~="boq-catalog-picker"]')
    const picker =
      host && this.application.getControllerForElementAndIdentifier(host, "boq-catalog-picker")
    if (!picker) return this.notify(this.messagesValue.catalog_unavailable)

    picker.setInsertContext(this.insertContext(this.row(rowId)))
    const knownIds = new Set(
      Array.from(this.element.querySelectorAll("tr[data-grid-row]"), (row) => row.dataset.rowId),
    )
    const key = this.storageKeyValue
    handoff.set(key, { knownIds, column: "quantity", closePicker: true })
    host.addEventListener(
      "boq-catalog-picker:closed",
      () => {
        if (handoff.get(key)?.closePicker) handoff.delete(key)
      },
      { once: true },
    )
    picker.open()
  }

  // From the row's stored list position, as #create's `insert_after` does — not its index among
  // the rendered siblings, which only matches while the positions run 1..n without gaps.
  insertContext(row) {
    if (!row) return {}
    if (row.dataset.kind === "group") return { parentId: row.dataset.rowId, position: 1 }

    return { parentId: row.dataset.parentId ?? "", position: Number(row.dataset.position) + 1 }
  }

  // -- drawer -----------------------------------------------------------------

  prepare(mode, returnTo) {
    this.reset()
    this.mode = mode
    this.returnTo = returnTo
  }

  show() {
    this.drawerTarget.hidden = false
  }

  // Back to the cell the drawer was opened from. After a Langtext save that cell's row is being
  // replaced by the answer; `after` is that render, and the row is selected once it has landed.
  close({ after = null } = {}) {
    const returnTo = this.returnTo
    this.reset()
    if (this.hasDrawerTarget) this.drawerTarget.hidden = true
    if (!returnTo) return
    if (after) after.then(() => this.reselect(returnTo))
    else this.reselect(returnTo)
  }

  reset() {
    this.mode = null
    this.returnTo = null
    this.pendingOpen = null
    if (!this.hasDrawerTarget) return

    this.longTextTarget.hidden = true
    this.longTextBodyTarget.innerHTML = ""
    this.frameTarget.removeAttribute("src")
    this.frameTarget.innerHTML = ""
  }

  reselect({ rowId, column }) {
    this.row(rowId)
      ?.querySelector(`td[data-column="${CSS.escape(column)}"]`)
      ?.click()
  }

  // -- plumbing ---------------------------------------------------------------

  async load(url) {
    try {
      const response = await fetch(url, {
        credentials: "same-origin",
        headers: { Accept: "text/html" },
      })
      if (!response.ok) throw new Error(`${url} responded ${response.status}`)
      return await response.text()
    } catch (error) {
      Honeybadger.notify(error, { context: { url } })
      this.failed()
      return null
    }
  }

  failed() {
    this.notify(this.messagesValue.request_failed)
  }

  // The toolbar's notice line, shared with position-structure.
  notify(text) {
    const notice = this.element.querySelector('[data-position-structure-target="notice"]')
    if (!notice || !text) return

    clearTimeout(this.noticeTimer)
    notice.textContent = text
    notice.hidden = false
    this.noticeTimer = setTimeout(() => (notice.hidden = true), 4000)
  }

  get csrfToken() {
    return document.querySelector("meta[name=csrf-token]")?.content
  }
}
